<template>
    <div class="bt-table-card">
        <div class="table-responsive">
            <table class="table bt-table">
                <thead>
                <tr>
                    <th>Master Budget Line</th>
                    <th>Budget Allocation</th>
                    <th>Budget Type</th>
                    <th>Interval</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Created</th>
                    <th>Version</th>
                    <th class="text-end">Amount</th>
                    <th class="text-center">Status</th>
                    <th class="text-end">Action</th>
                </tr>
                </thead>
                <tbody v-if="loading">
                <TableSkeleton :rows="6" :columns="11"/>
                </tbody>
                <tbody v-if="! loading">
                <tr v-for="item in rows" :key="item.id">
                    <td>
                        <div class="bt-line-name">{{ item.budget_line_name }}</div>
                        <div v-if="item.budget_line_code" class="bt-code">{{ item.budget_line_code }}</div>
                    </td>
                    <td>
                        <button type="button" class="bt-name" @click="emit('edit', item.id)">{{ item.name }}</button>
                        <div v-if="item.code" class="bt-code">{{ item.code }}</div>
                    </td>
                    <td>
                        <div class="bt-line-name">{{ item.budget_type_name }}</div>
                        <div v-if="item.dimension_name" class="bt-code">{{ item.dimension_name }}</div>
                    </td>
                    <td class="text-nowrap">{{ item.interval_name }}</td>
                    <td class="text-nowrap">{{ formatDateToDdMmYy(item.start_date) }}</td>
                    <td class="text-nowrap">{{ formatDateToDdMmYy(item.end_date) }}</td>
                    <td class="text-nowrap">{{ formatDateToDdMmYy(item.create_date) }}</td>
                    <td>
                        <div class="bt-version" @click.stop>
                            <button
                                type="button"
                                class="bt-version-btn"
                                :disabled="! item.versions.length"
                                @click="toggleMenu(item.id)"
                            >
                                <i class="fa-solid fa-code-branch"></i>
                                <span>{{ versionButtonLabel(item) }}</span>
                                <i class="fa-solid fa-chevron-down bt-caret"></i>
                            </button>
                            <div v-if="openMenuId === item.id" class="bt-version-menu">
                                <div class="bt-version-menu-title">Click a version to download it</div>
                                <button
                                    v-for="version in item.versions"
                                    :key="version.id"
                                    type="button"
                                    class="bt-version-item"
                                    :disabled="exportingVersionId === version.id"
                                    @click="onClickVersion(item, version)"
                                >
                                    <span class="bt-version-no">V{{ version.version_no }}</span>
                                    <StatusChip :status="version.status" :tones="budgetVersionTones"/>
                                    <span class="bt-version-amount">{{ commaFormat(version.total_amount) }}</span>
                                    <span v-if="exportingVersionId === version.id" class="spinner-border spinner-border-sm text-primary" role="status"></span>
                                    <span v-if="exportingVersionId !== version.id" class="bt-version-download"><i class="fa-solid fa-download"></i></span>
                                </button>
                            </div>
                        </div>
                    </td>
                    <td class="text-end text-nowrap">{{ commaFormat(currentAmount(item)) }}</td>
                    <td class="text-center">
                        <StatusChip :status="item.status" :tones="budgetVersionTones"/>
                        <div v-if="openRevision(item)" class="bt-revision-note">
                            V{{ openRevision(item).version_no }} {{ openRevision(item).status }}
                        </div>
                    </td>
                    <td class="text-end">
                        <button type="button" class="bt-action-btn" title="Open budget" @click="emit('edit', item.id)">
                            <i class="fa-regular fa-pen-to-square"></i>
                            <span>Open</span>
                        </button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <EmptyState
            v-if="! loading && ! rows.length"
            icon="fa-solid fa-wallet"
            title="No budget allocations found"
            message="Try a different filter, or add a new budget allocation on a master budget line."
        >
            <button type="button" class="btn bt-empty-btn" @click="emit('add')">
                <i class="fa-solid fa-plus"></i>
                <span>Add Budget Allocation</span>
            </button>
        </EmptyState>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import StatusChip from '@/components/atom/StatusChip.vue'
import EmptyState from '@/components/atom/EmptyState.vue'
import TableSkeleton from '@/components/atom/TableSkeleton.vue'
import figureFormatter from '@/services/utils/figureFormatter'
import { budgetVersionTones, formatDateToDdMmYy } from '@/services/utils/global.js'

defineProps({
    rows: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    },
    exportingVersionId: {
        type: Number,
        default: null
    }
})

const emit = defineEmits(['edit', 'add', 'export'])

const { commaFormat } = figureFormatter()

const openMenuId = ref(null)

const openStatuses = ['draft', 'pending', 'rejected']

const activeVersion = (item) => item.versions.find(version => version.status === 'active')

const openRevision = (item) => item.versions.find(version => ! version.is_current && openStatuses.includes(version.status))

const currentAmount = (item) => {
    const active = activeVersion(item)
    if (active) return active.total_amount
    if (! item.versions.length) return 0
    return item.versions[0].total_amount
}

const versionButtonLabel = (item) => {
    if (! item.versions.length) return 'No version'
    const active = activeVersion(item)
    const shown = active || item.versions[0]
    return `V${shown.version_no} · ${item.versions.length} version${item.versions.length > 1 ? 's' : ''}`
}

const toggleMenu = (id) => {
    openMenuId.value = openMenuId.value === id ? null : id
}

const onClickVersion = (item, version) => {
    emit('export', { budget: item, version })
}

const closeMenu = () => {
    openMenuId.value = null
}

onMounted(() => {
    document.addEventListener('click', closeMenu)
})

onUnmounted(() => {
    document.removeEventListener('click', closeMenu)
})
</script>

<style scoped>
.bt-table-card {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
}
.bt-table-card .table-responsive {
    overflow: visible;
}
.bt-table {
    margin-bottom: 0;
}
.bt-table thead th {
    padding: 12px 14px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #475467;
    white-space: nowrap;
}
.bt-table thead th:first-child {
    border-top-left-radius: 12px;
}
.bt-table thead th:last-child {
    border-top-right-radius: 12px;
}
.bt-table tbody td {
    padding: 14px;
    border-bottom: 1px solid #f2f4f7;
    font-size: 14px;
    line-height: 1.45;
    color: #344054;
    vertical-align: middle;
}
.bt-table tbody tr:last-child td {
    border-bottom: 0;
}
.bt-table tbody tr:hover {
    background: #fcfcfd;
}
.bt-line-name {
    font-weight: 500;
    color: #101828;
}
.bt-name {
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 14px;
    font-weight: 600;
    color: #101828;
    text-align: left;
    cursor: pointer;
}
.bt-name:hover {
    color: #7367f0;
}
.bt-code {
    margin-top: 2px;
    font-size: 13px;
    color: #667085;
}
.bt-version {
    position: relative;
    display: inline-block;
}
.bt-version-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid #d0d5dd;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    white-space: nowrap;
}
.bt-version-btn:hover:not(:disabled) {
    border-color: #7367f0;
    color: #7367f0;
}
.bt-version-btn:disabled {
    opacity: .6;
    cursor: not-allowed;
}
.bt-caret {
    font-size: 11px;
}
.bt-version-menu {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 1055;
    min-width: 320px;
    padding: 6px;
    border: 1px solid #eaecf0;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 12px 24px rgba(16, 24, 40, .12);
}
.bt-version-menu-title {
    padding: 6px 10px 8px;
    font-size: 12px;
    color: #667085;
}
.bt-version-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    font-size: 14px;
    color: #344054;
    text-align: left;
    cursor: pointer;
}
.bt-version-item:hover:not(:disabled) {
    background: #f4f3ff;
}
.bt-version-item:disabled {
    cursor: wait;
}
.bt-version-no {
    min-width: 30px;
    font-weight: 600;
    color: #101828;
}
.bt-version-amount {
    margin-left: auto;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}
.bt-version-download {
    color: #7367f0;
}
.bt-revision-note {
    margin-top: 4px;
    font-size: 12px;
    color: #b54708;
    text-transform: capitalize;
}
.bt-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid #e4e7ec;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    color: #475467;
    transition: all .15s ease;
}
.bt-action-btn:hover {
    border-color: #7367f0;
    background: #f4f3ff;
    color: #7367f0;
}
.bt-empty-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 16px;
    border-radius: 8px;
    background: #7367f0;
    border: 1px solid #7367f0;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
}
.bt-empty-btn:hover {
    background: #5e50ee;
    border-color: #5e50ee;
    color: #fff;
}
@media (max-width: 768px) {
    .bt-version-menu {
        min-width: 260px;
    }
}
</style>
